import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	Users,
	TrendingUp,
	TrendingDown,
	ArrowUp,
	ArrowDown,
	Minus,
} from 'lucide-react'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

import type { GetActiveUsersSummaryResponse } from '@/modules/dashboard/type'

type ActiveUsersSummaryCardProps = {
	data?: GetActiveUsersSummaryResponse
	comparisonData?: GetActiveUsersSummaryResponse
	enableComparison?: boolean
}

export default function ActiveUsersSummaryCard({
	data,
	comparisonData,
	enableComparison = false,
}: ActiveUsersSummaryCardProps) {
	if (!data) return null

	const formatThaiDateTime = (dateString: string) => {
		try {
			return format(new Date(dateString), 'dd MMM HH:mm', { locale: th })
		} catch {
			return '-'
		}
	}

	const calculateTrend = (current: number, compareValue?: number) => {
		if (!compareValue || compareValue === 0) {
			return {
				percentage: '0.0',
				isPositive: false,
				isNeutral: true,
				color: 'text-muted-foreground',
				icon: Minus,
			}
		}

		const variance = ((current - compareValue) / compareValue) * 100
		const isPositive = variance > 0
		const isNeutral = Math.abs(variance) < 2

		return {
			percentage: Math.abs(variance).toFixed(1),
			isPositive,
			isNeutral,
			color: isNeutral
				? 'text-muted-foreground'
				: isPositive
					? 'text-emerald-600 dark:text-emerald-400'
					: 'text-rose-600 dark:text-rose-400',
			icon: isNeutral ? Minus : isPositive ? ArrowUp : ArrowDown,
		}
	}

	const summaryCards = [
		{
			title: 'ค่าเฉลี่ย',
			value: data.averageActiveUsers.toLocaleString('th-TH', {
				maximumFractionDigits: 0,
			}),
			comparisonValue: comparisonData?.averageActiveUsers,
			icon: Users,
			description: 'จำนวนผู้ใช้งานเฉลี่ย',
			iconBg: 'bg-blue-500/10',
			iconColor: 'text-blue-600 dark:text-blue-400',
			trend: calculateTrend(
				data.averageActiveUsers,
				comparisonData?.averageActiveUsers,
			),
			tooltip: 'ค่าเฉลี่ยจำนวนผู้ใช้งานที่มีกิจกรรมในระบบ',
		},
		{
			title: 'สูงสุด',
			value: data.peakActiveUsers.toLocaleString('th-TH'),
			comparisonValue: comparisonData?.peakActiveUsers,
			icon: TrendingUp,
			description: formatThaiDateTime(data.peakTimestamp),
			iconBg: 'bg-emerald-500/10',
			iconColor: 'text-emerald-600 dark:text-emerald-400',
			trend: calculateTrend(
				data.peakActiveUsers,
				comparisonData?.peakActiveUsers,
			),
			tooltip: 'จำนวนผู้ใช้งานสูงสุดที่บันทึกได้',
		},
		{
			title: 'ต่ำสุด',
			value: data.minActiveUsers.toLocaleString('th-TH'),
			comparisonValue: comparisonData?.minActiveUsers,
			icon: TrendingDown,
			description: 'จำนวนผู้ใช้งานต่ำสุด',
			iconBg: 'bg-amber-500/10',
			iconColor: 'text-amber-600 dark:text-amber-400',
			trend: calculateTrend(
				data.minActiveUsers,
				comparisonData?.minActiveUsers,
			),
			tooltip: 'จำนวนผู้ใช้งานต่ำสุดที่บันทึกได้',
		},
	]

	const usingComparison = enableComparison && !!comparisonData

	return (
		<div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
			{summaryCards.map((card) => {
				const Icon = card.icon
				const TrendIcon = card.trend.icon
				return (
					<TooltipProvider key={card.title}>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="group relative overflow-hidden rounded-xl border bg-card p-4 shadow-sm transition-all duration-300 hover:scale-105 hover:shadow-xl sm:p-6">
									<div className="relative space-y-3">
										{/* Header with icon */}
										<div className="flex items-center justify-between">
											<span className="text-xs font-medium text-muted-foreground sm:text-sm">
												{card.title}
											</span>
											<div
												className={`rounded-lg p-1.5 sm:p-2 ${card.iconBg} transition-transform duration-300 group-hover:scale-110`}
											>
												<Icon
													className={`h-4 w-4 sm:h-5 sm:w-5 ${card.iconColor}`}
												/>
											</div>
										</div>

										{/* Value */}
										<div className="space-y-1">
											<div className="flex items-baseline gap-2">
												<div className="text-2xl font-bold tracking-tight sm:text-3xl">
													{card.value}
												</div>
												<span className="text-xs text-muted-foreground sm:text-sm">
													คน
												</span>
											</div>

											{/* Trend Indicator */}
											<div className="flex flex-col gap-1">
												{usingComparison && card.comparisonValue && (
													<div className="text-xs text-muted-foreground">
														เปรียบเทียบ:{' '}
														{card.comparisonValue.toLocaleString('th-TH')} คน
													</div>
												)}
												<div className="flex items-center gap-1.5">
													<div
														className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
															card.trend.isNeutral
																? 'bg-muted/50'
																: card.trend.isPositive
																	? 'bg-emerald-500/10'
																	: 'bg-rose-500/10'
														}`}
													>
														<TrendIcon
															className={`h-3 w-3 ${card.trend.color}`}
														/>
														<span className={card.trend.color}>
															{card.trend.isPositive ? '+' : ''}
															{card.trend.percentage}%
														</span>
													</div>
													<span className="text-xs text-muted-foreground">
														{usingComparison
															? 'vs ช่วงเปรียบเทียบ'
															: 'เทียบช่วงก่อน'}
													</span>
												</div>
											</div>
										</div>

										{/* Description */}
										<p className="text-xs text-muted-foreground">
											{card.description}
										</p>
									</div>
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>{card.tooltip}</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)
			})}
		</div>
	)
}
